import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { SubmitHandler, FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { Input, CheckboxInput, Select } from 'components/shared/Form';
import { MainContainer, Modal, Alert } from 'components/shared';
import { useValidation, useTranslation, usePermissions } from 'hooks';
import { translations } from './translations';
import { GroupedPermission } from 'interfaces/group';
import {
  FetchGroupActions,
  FetchGroupState,
  UpdateGroupActions,
  UpdateGroupState,
  DeleteGroupActions,
  DeleteGroupState,
} from 'store/ducks/settings/groups';
import {
  PaginatePermissionsState,
  PaginatePermissionsActions,
} from 'store/ducks/settings/permissions';
import { ListModulesActions, ListModulesState } from 'store/ducks/modules';

import * as S from './styles';

interface IParams {
  id: string;
}

export const EditGroup: React.FC = () => {
  const { id } = useParams<IParams>();
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors } = useValidation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { getTranslation } = useTranslation(translations);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedModule, setSelectedModule] = useState<string>();
  const [groupPermission, setGroupPermission] = useState<GroupedPermission>({});
  const [defaultPermission, setDefaultPermission] = useState([]);
  const { checkUserPermission } = usePermissions();

  const { loading: loadingModules, data: dataModules } = useSelector<
    RootStateOrAny,
    ListModulesState
  >((state: any) => state.listModules);

  const { data: dataPermissions } = useSelector<
    RootStateOrAny,
    PaginatePermissionsState
  >((state: any) => state.paginatePermissions);

  const { data: dataGroup, loading } = useSelector<
    RootStateOrAny,
    FetchGroupState
  >((state: any) => state.fetchGroup);

  const { loading: loadingDeleteGroup } = useSelector<RootStateOrAny>(
    (state: any) => state.deleteGroup
  ) as DeleteGroupState;

  const { loading: loadingUpdateGroup } = useSelector<RootStateOrAny>(
    (state: any) => state.updateGroup
  ) as UpdateGroupState;

  const onSuccess = useCallback(() => {
    history.push('/settings/groups');
  }, [history]);
  const onFetchSuccess = useCallback((data: any) => {
    const permissionsGroup = data.group_permissions.map((item: any) => ({
      id: `${item.permission.id}`,
      label: item.permission.name,
      value: `${item.permission.id}`,
    }));
    setDefaultPermission(() => permissionsGroup);
    if (formRef.current) {
      formRef.current.setData(data);
    }
  }, []);

  const handleSubmit = useCallback<SubmitHandler>(
    async (data: any) => {
      try {
        const { name } = data;
        delete data.general_module_id;
        delete data.name;

        const permissionIds = Object.values(data)
          .flat()
          .map((permissionId) => permissionId);

        formRef.current?.setErrors({});

        const newData = {
          name,
          permissionIds,
        };
        const schema = Yup.object().shape({
          name: Yup.string().required('Obrigatório'),
        });

        await schema.validate(newData, {
          abortEarly: false,
        });

        dispatch(UpdateGroupActions.request(id, newData, onSuccess));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, onSuccess, id]
  );

  const fetchData = useCallback(() => {
    if (id) {
      dispatch(FetchGroupActions.request(id, onFetchSuccess));
    }
  }, [dispatch, id, onFetchSuccess]);

  const getListModules = useCallback(() => {
    dispatch(ListModulesActions.request({ all: true }));
  }, [dispatch]);

  const getPermissions = useCallback(() => {
    dispatch(PaginatePermissionsActions.request({ all: true }));
  }, [dispatch]);

  const onHandleChangeModule = useCallback((data: any) => {
    setSelectedModule(String(data.value));
  }, []);

  const onHandleBuildPermission = useCallback(() => {
    const listPermission: GroupedPermission = {};

    dataPermissions?.forEach((item) => {
      if (!listPermission.hasOwnProperty(`${item.domain}`)) {
        Object.assign(listPermission, { [`${item.domain}`]: [] });
      }

      listPermission[`${item.domain}`].push({
        id: `${item?.id}`,
        label: item?.name,
        value: `${item?.id}`,
        module_id: `${item.module.id}`,
        description: item.description,
      });
    });

    setGroupPermission(() => listPermission);
  }, [dataPermissions]);

  const onDeleteSuccess = useCallback(() => {
    history.goBack();
  }, [history]);

  const deleteData = useCallback(() => {
    dispatch(DeleteGroupActions.request(id, onDeleteSuccess));
  }, [dispatch, id, onDeleteSuccess]);

  useEffect(() => getListModules(), [getListModules]);
  useEffect(() => getPermissions(), [getPermissions]);
  useEffect(() => fetchData(), [fetchData]);

  useEffect(() => {
    if (dataPermissions) {
      onHandleBuildPermission();
    }
  }, [dataPermissions, onHandleBuildPermission]);

  return (
    <MainContainer>
      <Modal isOpen={modalOpen}>
        <Alert
          title={`${getTranslation('removerTitulo')} - ${dataGroup?.name}`}
          text={getTranslation('removerDesc')}
          close={() => setModalOpen(false)}
          action={deleteData}
          labelAction={getTranslation('remover')}
          isLoading={loadingDeleteGroup}
        />
      </Modal>

      <S.PageHeader>
        <h1>
          <S.IconSetting />
          {getTranslation('configuracoes')}
          <span>{getTranslation('editarPerfilUsuario')}</span>
        </h1>
        <S.HeaderButtons>
          <S.ButtonMini btStyle="dark" onClick={() => history.goBack()}>
            <S.IconArrowLeft />
            {getTranslation('voltar')}
          </S.ButtonMini>
        </S.HeaderButtons>
      </S.PageHeader>
      <S.PageContent>
        <Form
          ref={formRef}
          onSubmit={handleSubmit}
          placeholder=""
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
        >
          <S.BoxContainer>
            <S.FormRow>
              <Input name="name" label={getTranslation('nome')} />
            </S.FormRow>
            <S.Title>{getTranslation('permissoesPerfil')}</S.Title>
            <S.FormRow>
              <Select
                name="general_module_id"
                label={getTranslation('modulo')}
                isLoading={loadingModules}
                isDisabled={loadingModules}
                options={dataModules}
                onChange={onHandleChangeModule}
              />
            </S.FormRow>
            <S.CheckContainer>
              {Object.entries(groupPermission).map(
                ([domain, permissions], i) => {
                  return (
                    <CheckboxInput
                      hideInList={
                        !(
                          selectedModule &&
                          permissions[0].module_id === selectedModule
                        )
                      }
                      direction="column"
                      fixWidth="165px"
                      key={domain}
                      options={permissions}
                      defaultOptions={defaultPermission}
                      name={domain}
                      label={domain}
                    />
                  );
                }
              )}
            </S.CheckContainer>
          </S.BoxContainer>

          <S.FormFooter>
            <S.FormRow>
              <S.Button
                btStyle="cancel"
                type="button"
                onClick={() => history.goBack()}
              >
                {getTranslation('cancelar')}
              </S.Button>
              {checkUserPermission('deleteProfileUser') && (
                <S.Button
                  btStyle="danger"
                  type="button"
                  onClick={() => setModalOpen(true)}
                >
                  {getTranslation('remover')}
                </S.Button>
              )}
              <S.Button type="submit">
                {loading || loadingUpdateGroup ? (
                  <S.Loading />
                ) : (
                  getTranslation('salvar')
                )}
              </S.Button>
            </S.FormRow>
          </S.FormFooter>
        </Form>
      </S.PageContent>
    </MainContainer>
  );
};
